import React from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ModuleStructure, DocumentItem } from '../types';
import { displayStructureTitle } from '../utils/structureTitle';

interface AIReasoningModalProps {
  open: boolean;
  onClose: () => void;
  module: ModuleStructure | null;
  moduleIndex: number;
  /** All documents from the wizard so we can match ragDocumentTitles to their metadata. */
  documents?: DocumentItem[];
}

const AIReasoningModal: React.FC<AIReasoningModalProps> = ({ open, onClose, module, moduleIndex, documents = [] }) => {
  if (!module) return null;

  const hasOutcomes = (module.learningOutcomesServed?.length ?? 0) > 0;
  const hasDocs = (module.ragDocumentTitles?.length ?? 0) > 0;
  const hasBottomSection = hasOutcomes || hasDocs;

  const findDoc = (title: string): DocumentItem | undefined =>
    documents.find((d) => d.title === title);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          width: '100%',
          padding: '25px',
          background: 'var(--Fill-Card-card-highlight, #1C1C1C)',
          borderRadius: '6px',
          outline: '1px var(--Stroke-Border-border, #444444) solid',
          outlineOffset: '-1px',
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: 'none',
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>

        {/* ── Header: AI Reasoning label + module title + close ── */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', gap: '7px' }}>
          <Box sx={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', gap: '5px' }}>
            {/* "AI Reasoning" label */}
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              {/* Small orange square icon */}
              <Box sx={{
                width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Box sx={{
                  width: 13, height: 13,
                  outline: '1.5px var(--Stroke-highlight, #F79420) solid',
                  outlineOffset: '-0.75px',
                  borderRadius: '1px',
                }} />
              </Box>
              <Typography sx={{
                color: 'var(--Stroke-highlight, #F79420)',
                fontSize: 12,
                fontFamily: 'Inter',
                fontWeight: 600,
                wordWrap: 'break-word',
              }}>
                Elliott&apos;s reasoning
              </Typography>
            </Box>

            {/* Module title */}
            <Typography sx={{
              color: 'var(--Text-text-primary, #FDFDFD)',
              fontSize: 20,
              fontFamily: 'Inter',
              fontWeight: 600,
              lineHeight: '24px',
              wordWrap: 'break-word',
            }}>
              Module {moduleIndex + 1}:<br />{displayStructureTitle(module.title)}
            </Typography>
          </Box>

          {/* Close button */}
          <Box
            component="button"
            onClick={onClose}
            sx={{
              background: 'none', border: 'none', cursor: 'pointer',
              width: 24, height: 24, padding: '6px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <CloseIcon sx={{ color: 'var(--Stroke-solid-white, #FDFDFD)', fontSize: 12 }} />
          </Box>
        </Box>

        {/* ── Module reasoning ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <Typography sx={{
            flex: '1 1 0',
            color: 'var(--Text-text-secondary, #B6B6B6)',
            fontSize: 14,
            fontFamily: 'Inter',
            fontWeight: 400,
            lineHeight: '19.88px',
            wordWrap: 'break-word',
          }}>
            {module.reasoning}
          </Typography>
        </Box>

        {/* ── Lessons ── */}
        {module.lessons.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {module.lessons.map((lesson, i) => (
              <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <Typography sx={{
                  color: 'var(--Stroke-highlight, #F79420)',
                  fontSize: 14,
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  wordWrap: 'break-word',
                }}>
                  Lesson {i + 1}: {displayStructureTitle(lesson.title)}
                </Typography>
                {lesson.reasoning && (
                  <Typography sx={{
                    color: 'var(--Text-text-primary, #FDFDFD)',
                    fontSize: 12,
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    lineHeight: '16.80px',
                    wordWrap: 'break-word',
                  }}>
                    {lesson.reasoning}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* ── Bottom: Learning Outcomes + Documents ── */}
        {hasBottomSection && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', gap: '30px' }}>

            {/* Learning Outcomes */}
            {hasOutcomes && (
              <Box sx={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Typography sx={{
                  color: 'var(--Text-text-primary, #FDFDFD)',
                  fontSize: 12,
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  lineHeight: '20px',
                  wordWrap: 'break-word',
                }}>
                  Serving goals:
                </Typography>
                {module.learningOutcomesServed?.map((lo, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '5px', overflow: 'hidden', borderRadius: '5px' }}>
                    {/* Bookmark-style orange icon */}
                    <Box sx={{
                      width: 18, height: 18,
                      pl: '7px', pr: '7px', pt: '6px', pb: '6px',
                      position: 'relative', overflow: 'hidden',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Box sx={{
                        width: '7.67px', height: '9px',
                        outline: '2px var(--Stroke-accent, #F79420) solid',
                        outlineOffset: '-1px',
                      }} />
                    </Box>
                    <Typography sx={{
                      flex: '1 1 0',
                      color: 'var(--Text-text-secondary, #B6B6B6)',
                      fontSize: 12,
                      fontFamily: 'Inter',
                      fontWeight: 500,
                      lineHeight: '15.60px',
                      wordWrap: 'break-word',
                    }}>
                      {lo}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Documents Informing */}
            {hasDocs && (
              <Box sx={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Typography sx={{
                  color: 'var(--Text-text-primary, #FDFDFD)',
                  fontSize: 12,
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  lineHeight: '20px',
                  wordWrap: 'break-word',
                }}>
                  Documents informing:
                </Typography>
                {module.ragDocumentTitles?.map((title, i) => {
                  const doc = findDoc(title);
                  const badge = doc?.fileTypeLabel || null;
                  return (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: '5px' }}>
                      {/* Document icon */}
                      <Box sx={{
                        width: 16, height: 16,
                        pl: '4px', pr: '4px', pt: '2px', pb: '2px',
                        position: 'relative', overflow: 'hidden',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, mt: '1px',
                      }}>
                        <Box sx={{
                          width: '10.67px', height: '13.33px',
                          outline: '1px var(--Stroke-icon-dim, #868686) solid',
                          outlineOffset: '-0.5px',
                        }} />
                      </Box>

                      <Box sx={{ flex: '1 1 0', display: 'flex', flexDirection: 'column' }}>
                        {/* Title row + badge */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <Typography sx={{
                            color: 'var(--Text-text-primary, #FDFDFD)',
                            fontSize: 12,
                            fontFamily: 'Inter',
                            fontWeight: 500,
                            lineHeight: '16px',
                            wordWrap: 'break-word',
                          }}>
                            {title}
                          </Typography>
                          {badge && (
                            <Box sx={{
                              px: '5px', py: '3px',
                              background: 'var(--Fill-Tag-social-tag, #0F0F0F)',
                              borderRadius: '20px',
                              outline: '1px var(--Stroke-Border-border-dim, #2A2A2A) solid',
                              outlineOffset: '-1px',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <Typography sx={{
                                color: 'var(--Text-text-primary, #FDFDFD)',
                                fontSize: 8,
                                fontFamily: 'Inter',
                                fontWeight: 700,
                                lineHeight: '8px',
                                wordWrap: 'break-word',
                              }}>
                                {badge}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Instructions */}
                        {/* Two lines at most, full text on hover. A fixed height let long
                            instructions spill over the next document. */}
                        {doc?.instructions && (
                          <Typography title={doc.instructions} sx={{
                            color: 'var(--Text-text-tertiary, #868686)',
                            fontSize: 10,
                            fontFamily: 'Inter',
                            fontWeight: 400,
                            lineHeight: '14px',
                            wordWrap: 'break-word',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}>
                            {doc.instructions}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

      </Box>
    </Dialog>
  );
};

export default AIReasoningModal;
